import React from 'react'
import { CircularProgress, Radio, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { ButtonWithLoading } from '@e3dc-react/shell/Elements/Buttons/Buttons'
import { BlankView } from 'src/Components/blank-view'
import { FoundDeviceModel } from 'src/Libs/installations/models/device-detector.model'
import { usePowerMeterScanDialog } from 'src/Libs/installations/hooks/installation-configuration/power-meters/use-power-meter-scan-dialog'

export interface PowerMeterScanDialogProps {
  onClose?: () => void
  emcSerialnumber: string
  onSelect: (serialNumber: string, ipAddress?: string, index?: number) => void
  linkedPowerMetersBySerialNumber?: Record<string, string>
}

const PowerMeterScanDialog: React.FC<PowerMeterScanDialogProps> = ({ emcSerialnumber, onSelect, onClose, linkedPowerMetersBySerialNumber = {} }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    foundPowerMeters,
    selectedSerialNumber,
    selectSerialNumber,
    hasScanError,
    isLoading,
    isScanning,
    hasScanResults,
    handleCancel,
    handleRescan,
    handleConfirm,
  } = usePowerMeterScanDialog({ onClose, emcSerialNumber: emcSerialnumber, onSelect })

  const primaryAction = hasScanError
    ? {
        name: t('installation-configuration.storage.scan-network-dialog.retry'),
        onClick: handleRescan,
      }
    : {
        name: t('common.confirm'),
        onClick: handleConfirm,
        disabled: isLoading || isScanning || !hasScanResults || !selectedSerialNumber,
      }

  // In the event of an error, "Retry" is displayed instead of "Rescan. Both would trigger a new scan."
  const rescanButton = hasScanError ? undefined : (
    <ButtonWithLoading
      variant="outlined"
      label={t('installation-configuration.powermeter.scan.dialog.rescan')}
      onClick={handleRescan}
      disabled={isLoading || isScanning}
      dataQa={'button-modal-rescan'}
      fullWidth={isMobile}
    />
  )

  const renderLoadingState = (labelKey: string): React.ReactNode => (
    <Stack alignItems="center" justifyContent="center" gap={2} py={4}>
      <CircularProgress />
      <Typography variant="body2">{t(labelKey)}</Typography>
    </Stack>
  )

  const renderDialogContent = (): React.ReactNode => {
    if (hasScanError) {
      return <BlankView imageSize={'SMALL'} title={t('installation-configuration.storage.scan.dialog.error')} imageVariant={'WORRIED'} />
    }

    if (isLoading) {
      return renderLoadingState('installation-configuration.powermeter.scan.dialog.loading')
    }

    if (isScanning) {
      return renderLoadingState('installation-configuration.powermeter.scan.dialog.scanning')
    }

    if (hasScanResults && foundPowerMeters.length === 0) {
      return <BlankView imageSize={'SMALL'} title={t('installation-configuration.storage.scan.dialog.no-results')} imageVariant={'NEUTRAL'} />
    }

    if (hasScanResults) {
      return (
        <PowerMeterScanResultsList
          devices={foundPowerMeters}
          selectedSerialNumber={selectedSerialNumber}
          onSelect={selectSerialNumber}
          linkedPowerMetersBySerialNumber={linkedPowerMetersBySerialNumber}
        />
      )
    }

    return null
  }

  return (
    <Dialog
      onClose={handleCancel}
      maxWidth="sm"
      title={t('installation-configuration.powermeter.scan.dialog.title')}
      additionalActionsLeft={rescanButton}
      actions={{
        primary: primaryAction,
        secondary: {
          name: t('common.cancel'),
          onClick: handleCancel,
        },
      }}
    >
      <Stack sx={{ py: 2 }}>{renderDialogContent()}</Stack>
    </Dialog>
  )
}

// #region PowerMeterScanResultsList
interface PowerMeterScanResultsListProps {
  devices: FoundDeviceModel[]
  selectedSerialNumber: string | null
  onSelect: (serialNumber: string) => void
  linkedPowerMetersBySerialNumber: Record<string, string>
}

const PowerMeterScanResultsList: React.FC<PowerMeterScanResultsListProps> = ({
  devices,
  selectedSerialNumber,
  onSelect,
  linkedPowerMetersBySerialNumber,
}) => {
  const { t } = useCustomTranslation()

  return (
    <Stack gap={2}>
      {devices.map(device => {
        const linkedPowerMeterName = linkedPowerMetersBySerialNumber[device.serialNumber]
        const isAlreadyAssigned = !!linkedPowerMeterName

        return (
          <Stack
            key={device.serialNumber}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            onClick={() => !isAlreadyAssigned && onSelect(device.serialNumber)}
            sx={theme => ({
              cursor: isAlreadyAssigned ? 'not-allowed' : 'pointer',
              opacity: isAlreadyAssigned ? 0.5 : 1,
              padding: `${theme.spacing(1)} ${theme.spacing(4)}`,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: selectedSerialNumber === device.serialNumber ? theme.palette.primary.main : theme.palette.grey.grey06,
              borderRadius: 1,
              backgroundColor: theme.palette.grey.grey07,
            })}
          >
            <Stack>
              <Typography variant="body2" fontWeight={600}>
                {device.serialNumber}
              </Typography>
              {linkedPowerMeterName && (
                <Typography variant="caption" color="text.secondary">
                  {t('installation-configuration.powermeter.scan.already-assigned')}: {linkedPowerMeterName}
                </Typography>
              )}
            </Stack>
            <Radio
              checked={selectedSerialNumber === device.serialNumber}
              onChange={() => onSelect(device.serialNumber)}
              disabled={isAlreadyAssigned}
              inputProps={{ 'aria-label': device.serialNumber }}
            />
          </Stack>
        )
      })}
    </Stack>
  )
}
// #endregion

export default PowerMeterScanDialog
